<template>
  <div>
    <StepHeader :title="'How Much Do You Make per Month from\nThese Additional Income Sources?'" />

    <AppAdditionalIncome
      title="Additional Income Source 1"
      data-layer-index="1"
      class="first-input"
      :amount="notes[0].amount"
      @amount-input="handleInput(0, 'amount', $event)"
      :amount-error="amountError0"
      :description="notes[0].description"
      @description-input="handleInput(0, 'description', $event)"
      :description-error="descriptionError0"
    />

    <AppAdditionalIncome
      title="Additional Income Source 2 (Optional)"
      data-layer-index="2"
      :amount="notes[1].amount"
      @amount-input="handleInput(1, 'amount', $event)"
      :amount-error="amountError1"
      :description="notes[1].description"
      @description-input="handleInput(1, 'description', $event)"
      :description-error="descriptionError1"
    />

    <AppAdditionalIncome
      title="Additional Income Source 3 (Optional)"
      data-layer-index="3"
      :amount="notes[2].amount"
      @amount-input="handleInput(2, 'amount', $event)"
      :amount-error="amountError2"
      :description="notes[2].description"
      @description-input="handleInput(2, 'description', $event)"
      :description-error="descriptionError2"
    />

    <StepFooter>
      <AppButtonContinue
        v-data-layer
        :data-layer="datalayerContinue"
        @click="handleContinue"
      >
        Continue
      </AppButtonContinue>
    </StepFooter>
  </div>
</template>

<script>
import StepHeader from '@/components/common/step/StepHeader'
import StepFooter from '@/components/common/step/StepFooter'
import AppButtonContinue from '@/components/common/buttons/AppButtonContinue'
import AppAdditionalIncome from '../inputs/AppAdditionalIncome'
import scroll from '@/mixins/scroll'

export default {
  // Name
  name: 'additional-income-amounts',

  // Components
  components: {
    StepHeader,
    StepFooter,
    AppAdditionalIncome,
    AppButtonContinue
  },

  // Mixins
  mixins: [scroll],

  // Data
  data () {
    return {
      datalayerStepName: 'additional_income_amount',
      fieldNameRaw: 'additionalIncome',
      fieldNameFormatted: 'csr_notes__c',
      submitted: false,
      errors: {
        isRequired: 'Required.',
        invalidNumber: 'Invalid number',
        descriptionRequired: 'Please describe this income source',
        amountRequired: 'Please enter income amount',
        maxCharacters: 'Cannot be longer than 500 characters'
      }
    }
  },

  // Computed
  computed: {
    notes () {
      const value = this.$store.state.application[this.fieldNameRaw]
      if (!value) return
      return JSON.parse(value)
    },

    // ---------------
    // Begin errors
    // ---------------

    // errors for the 1st inputs
    amountError0 () {
      const value = this.notes[0].amount
      if (value && !this.isValidNumber(value)) return this.errors.invalidNumber
      if (this.submitted && !value) return this.errors.isRequired
      return ''
    },

    descriptionError0 () {
      const value = this.notes[0].description
      if (this.submitted && !value.trim()) return this.errors.isRequired
      if (value.length > 500) return this.errors.maxCharacters
      return ''
    },

    // errors for the 2nd inputs
    amountError1 () {
      const value = this.notes[1].amount
      if (value && !this.isValidNumber(value)) return this.errors.invalidNumber
      if (this.submitted && this.notes[1].description.trim() && !value) {
        return this.errors.amountRequired
      }
      return ''
    },

    descriptionError1 () {
      const value = this.notes[1].description
      if (this.submitted && this.notes[1].amount && !value.trim()) {
        return this.errors.descriptionRequired
      }
      if (value.length > 500) return this.errors.maxCharacters
      return ''
    },

    // errors for the 3rd inputs
    amountError2 () {
      const value = this.notes[2].amount
      if (value && !this.isValidNumber(value)) return this.errors.invalidNumber
      if (this.submitted && this.notes[2].description.trim() && !value) {
        return this.errors.amountRequired
      }
      return ''
    },

    descriptionError2 () {
      const value = this.notes[2].description
      if (this.submitted && this.notes[2].amount && !value.trim()) {
        return this.errors.descriptionRequired
      }
      if (value.length > 500) return this.errors.maxCharacters
      return ''
    },

    // ---------------
    // End errors
    // ---------------

    monthly_income () {
      return this.$store.state.application.monthly_income
    },

    datalayerContinue () {
      return `${this.datalayerStepName}_continue`
    },

    stepIsValid () {
      return (
        !this.amountError0 &&
        !this.amountError1 &&
        !this.amountError2 &&
        !this.descriptionError0 &&
        !this.descriptionError1 &&
        !this.descriptionError2
      )
    }
  },

  // Created
  created () {
    if (!this.notes) {
      const schema = [
        { amount: '', description: '' },
        { amount: '', description: '' },
        { amount: '', description: '' }
      ]
      this.updateNotes(schema)
    }
  },

  // Methods
  methods: {
    /**
     * Update Notes
     * Updates two fields of the application state:
     *   'additionalIncome' -  JSON string, read/write additional income data
     *   'csr_notes__c' - formatted string for output to Salesforce
     * @param array - 'notes' data schema
     */
    updateNotes (value) {
      this.$store.updateApplication(this.fieldNameRaw, JSON.stringify(value))
      this.$store.updateApplication(this.fieldNameFormatted, this.formatAdditionalIncome(value))
    },

    /**
     * Is Valid Number
     * Checks if the value is valid
     * @returns {boolean}
     */
    isValidNumber (value) {
      return /^\d+$/.test(value)
    },

    /**
     * Handle Input
     * Updates a single 'notes' value
     * @param number - index of note to change (0, 1, or 2)
     * @param string - value to change ('amount' or 'description')
     * @param string - input value
     */
    handleInput (index, key, eventValue) {
      const notes = this.notes.map((e) => Object.assign({}, e))
      notes[index][key] = eventValue
      this.updateNotes(notes)
    },

    /**
     * Format Additional Income
     * Makes the additional income nice and pretty
     * @param {Array} - incomeArray
     * @returns {String}
     */
    formatAdditionalIncome (incomeArray) {
      let formattedValue = `Original Income: $${this.monthly_income}\n`
      incomeArray.forEach((income, index) => {
        if (income.amount || income.description) {
          if (index !== 0) formattedValue += '\n\n'
          formattedValue += `Additional Income ${index + 1}: ${income.amount || 'N/A'}\n`
          formattedValue += `Additional Source ${index + 1}: ${income.description || 'N/A'}`
        }
      })
      return formattedValue
    },

    /**
     * Handle Continue
     * Validate the step and continue
     */
    handleContinue () {
      this.submitted = true
      if (this.stepIsValid) {
        this.$emit('next')
      } else {
        this.$nextTick(() => this.$_scroll_toElement('.has-error', 1000))
      }
    }
  }
}
</script>

<style scoped lang="scss">
.first-input {
  margin-top: 0;

  @include breakpoint('desktop-up') {
    margin-top: 62px;
  }
}

.step-footer {
  @include breakpoint('tablet-landscape-up') {
    padding-top: 0;
  }

  @media (min-width:600px){
    padding-bottom: 75px;
  }
}
</style>
